/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    take, fork, call, put, select,
    takeLatest,
} from 'redux-saga/effects';
import Cookies from 'universal-cookie';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import accountServiceClient from '../../../../../apis/account-apis/accountServiceClient';

// Actions
import { getPaymentUsedToPlaceOrder } from '../Payment/OrderPlacedPayment/OrderPlacedPayment-Actions';
import { clearErrors } from '../../../Common/ducks/Error/Error-Actions';
import { unsubscribeEmail } from '../../../Checkout/ducks/Payment/ducks/Subscription/Subscription-Action';
import { loadWallet } from '../../../Checkout/ducks/Global/Global-Actions';
import {
    appProcessPage,
} from './Pages-Actions';
import { setEmailOptInSubscribe } from '../../../Checkout/ducks/Payment/ducks/Account/Account-Actions';
import { pageInitializationCompleted, pageInitializationStart } from '../Payment/PaymentReadiness/PaymentReadiness-Action';
import { paymentClearUpdateFields, setPaymentMethod } from '../../../Checkout/ducks/Payment/ducks/PaymentForm/PaymentForm-Actions';

// Selectors

import { getFeatureFlags, getOnePageCheckoutSaleForceABTest } from '../App/App-Selectors';
import { getEmailOptInSubscribeStatus } from '../../../Checkout/ducks/Payment/ducks/Account/Account-Selectors';
import {
    getCurrentBrandId,
    getPaymentInfo,
    getPaymentMethod,
    getPaymentMethods,
    getPlaceOrderStatus,
    continueToPayment,
} from '../../../Checkout/ducks/Common/Common-Selectors';

import {
    getAppliedPromotions,
    getCartOrder,
    getIsDonationOnlyItemOnCart,
    getIsPassportOnlyItemOnCart,
    getOrderId,
    getOrderStatus,
    getRecipients,
} from '../Cart/Cart-Selectors';

import {
    getProfileInfo,
    getIsWriteScopeCheckout,
    getUserType,
    getIsAuthenticatedStatus,
    getContactId,
} from '../../../Checkout/ducks/Global/Global-Selectors';
import { getPaypalPaymentDetails } from '../Payment/PaymentMethods/Paypal/Paypal-Selectors';

// Operations
import { retrieveCart } from '../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { checkJWT } from '../../../Checkout/ducks/Global/Global-Operations';
import { workerApplePayAvailable } from '../Payment/PaymentMethods/ApplePay/ApplePay-Operations';
import { fetchMostUsedPaymentMethods } from '../Payment/PaymentMethods/UserPaymentMethod/UserPaymentMethod-Operations';

// Helpers

import { prepareRetreiveCart } from '../Cart/ducks/RetrieveCart/PrepareRetreiveCart-Operations';
import { loadCart } from '../Cart/Cart-Actions';
import { loadCartCount, loadOrderId } from '../../../Order/Order-Actions';
import { billingAddressClearForm } from '../Payment/BillingAddress/BillingAddress-Actions';
import { attributionApplyPromoCompleted, logBannerCodeTried } from '../Attribution/Attribution-Actions';
import {
    setSkipValidateCart,
    triggerValidateOrder,
    triggerValidateOrderCompleted,
} from '../Cart/ducks/ValidateCart/ValidateCart-Actions';
import { getSkipValidateCart } from '../Cart/ducks/ValidateCart/ValidateCart-Selectors';
import { cartStatusCompleteList } from '../../../../../app/helpers/common/helper';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { clearAllErrors } from '../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { loadAddressBookEntry, loadBillingAddressBookEntry } from '../Member/AddressBookEntry/AddressBookEntry-Actions';
import { workerAttributionApplyPromoCode, workerCartAttributes } from '../Attribution/Attribution-Operation';
import { emitClickstreamCheckoutEvent } from '../../../TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import updateAttribute from '../../../../../apis/cart-service-apis/updateAttribute';

function* fillSavedBillingAddress() {
    try {
        const isAuthenticated = yield select(getIsAuthenticatedStatus);

        if (isAuthenticated) {
            const jwtToken = yield call(checkJWT);
            const contactId = yield select(getContactId);
            const wcEnv = {
                host: mbpUtil.getEnv('APP_WC_HOST'),
            };

            yield put(loadBillingAddressBookEntry());
        }
    } catch (ex) {
        console.log('error in fillSavedBillingAddress:', ex);
        mbpLogger.logError({
            function: 'fillSavedBillingAddress',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            message: 'Failed - fillSavedBillingAddress',
            jsError: ex,
        });
    }
}

// Handle all the page load processing required for the Payment page.
function* onPaymentPageProcessing(props) {
    const cartId = yield select(getOrderId);

    try {
        const flagState = yield select(getFeatureFlags);
        const { history, page } = props.data;
        if (flagState['is-checkout-monitoring-enabled']) {
            mbpLogger.logInfo({
                function: 'onPaymentPageProcessing',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: `onPaymentPageProcessing [${cartId}] - Start`,
            });
        }

        const paypalPayment = yield select(getPaypalPaymentDetails);

        if (paypalPayment?.payload?.nonce) {
            const availablePaymentMethods = yield select(getPaymentMethods);
            yield put(setPaymentMethod(availablePaymentMethods.PayPal));
        } else {
            // Validate Order
            const isSkipValidateCart = yield select(getSkipValidateCart);
            let validateOrder = {};

            if (isSkipValidateCart || page === 'single-shipping-payment' || page === 'single-payment-review') {
                validateOrder.status = true;
                yield put(setSkipValidateCart(false));
            } else {
                yield put(triggerValidateOrder(history));
                validateOrder = yield take(triggerValidateOrderCompleted().type);
            }
            const appliedPromos = yield select(getAppliedPromotions);
            if (validateOrder?.status) {
                // load billing address
                yield call(fillSavedBillingAddress);
                if (!appliedPromos || appliedPromos?.length <= 0) {
                    yield fork(workerAttributionApplyPromoCode);
                    yield take(attributionApplyPromoCompleted().type);
                    if (['single-shipping-payment', 'single-payment-review'].includes(page)) {
                        yield call(retrieveCart, false, true);
                    }
                }
            }
        }

        if (flagState['is-checkout-monitoring-enabled']) {
            mbpLogger.logInfo({
                function: 'onPaymentPageProcessing',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: `Payment Page Initialization Completed ${cartId}`,
            });
        }
    } catch (ex) {
        yield put(flagOffShowLoadingMessage());
        mbpLogger.logError({
            function: 'onPaymentPageProcessing',
            appName: 'mbp-checkout',
            module: 'mbp-checkout',
            message: 'Failed - onPaymentPageProcessing',
            jsError: ex,
            orderId: cartId,
        });
    }
}

function* clearCheckoutDependantState() {
    yield put(loadCart({}));
    yield put(loadOrderId(''));
    yield put(loadCartCount(0));
    yield put(logBannerCodeTried(false));
    yield put(paymentClearUpdateFields());
    yield put(billingAddressClearForm());
    yield put(clearAllErrors());
}

export function* checkoutRouteHandler(history) {
    try {
        const allOrderItems = yield select(getRecipients);
        const isPassportOnlyItem = yield select(getIsPassportOnlyItemOnCart);
        const isDonationOnlyItem = yield select(getIsDonationOnlyItemOnCart);
        const orderId = yield select(getOrderId);
        const pagePath = history.location.pathname;
        const orderStatus = yield select(getOrderStatus);
        const isOrderCompleted = orderStatus && cartStatusCompleteList.includes(orderStatus);
        if (isOrderCompleted && pagePath.indexOf('/order-confirmation') > 0) {
            history.push(`/checkout/order-confirmation/${orderId}`);
        } else if (isOrderCompleted && pagePath.indexOf('/cart') > 0) {
            yield call(clearCheckoutDependantState);
        } else if (isOrderCompleted) {
            const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');
            if (REACT_APP_INJECTABLE === 'false') {
                window.location.href = '/?redirect=thankyou';
            } else {
                history.push('/?redirect=thankyou');
            }
        } else if (pagePath.indexOf('/cart') > 0 && allOrderItems.length === 0) {
            yield call(clearCheckoutDependantState);
        } else if (pagePath.indexOf('/shipping') > 0) {
            // IF Passport only item in cart proceed to payment page
            const featureFlags = yield select(getFeatureFlags);
            const onePageCheckout = yield select(getOnePageCheckoutSaleForceABTest) || {};
            const isOnePageCheckoutEnabled = featureFlags['is-one-page-checkout-enabled'] === 'newflow'
                || (
                    featureFlags['is-one-page-checkout-enabled'] === 'salesforce'
                    && onePageCheckout?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
                );

            if ((isPassportOnlyItem || isDonationOnlyItem) && !isOnePageCheckoutEnabled) {
                history.push(`/checkout/payment/${orderId}`);
            } else if (allOrderItems.length === 0) {
                yield call(clearCheckoutDependantState);
                history.push(`/checkout/cart/${orderId}`);
            }
        } else if (pagePath.indexOf('/payment') > 0) {
            // Validate all order items to have recipient address, delivery date and gift message.
            const isContinueToPayment = yield select(continueToPayment);
            if (!isContinueToPayment) {
                history.push(`/checkout/shipping/${orderId}`);
            } else if (allOrderItems.length === 0) {
                yield call(clearCheckoutDependantState);
                history.push(`/checkout/cart/${orderId}`);
            }
        } else if (pagePath.indexOf('/review-order') > 0) {
            const placeOrderStatus = yield select(getPlaceOrderStatus);
            const selectedPaymentMethod = yield select(getPaymentMethod);
            const paymentForm = yield select(getPaymentInfo);

            // Validate all order items to have recipient address, delivery date and gift message.
            const isContinueToPayment = yield select(continueToPayment);

            if (!isContinueToPayment) {
                history.push(`/checkout/shipping/${orderId}`);
            } else if (selectedPaymentMethod.id === 'CreditCard' && !paymentForm.nameOnCreditCard && !placeOrderStatus && allOrderItems.length > 0) {
                history.push(`/checkout/payment/${orderId}`);
            } else if (allOrderItems.length === 0) {
                yield call(clearCheckoutDependantState);
                history.push(`/checkout/cart/${orderId}`);
            }
        } else if (pagePath.indexOf('/cart') < 0) {
            history.push(`/checkout/cart/${orderId}`);
        }
    } catch (ex) {
        const orderId = yield select(getOrderId);
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'checkoutRouteHandler',
            jsError: ex,
            message: 'Error occured while handling checkout route',
            orderId,
        });
    }
}

const brandsEmailOpt = {
    FB: '18F',
    FBQ: '18F',
    HD: 'HND',
    WF: 'WLF',
    SY: 'STK',
    STY: 'STK',
    BRY: '18F',
    // CSG: 'CSG', // TODO look if have email opt
};

function* workerProcessShippingPage(action) {
    const { page, history } = action.data;

    yield call(prepareRetreiveCart, page);
    yield call(retrieveCart, false, true);

    // TODO: check is the attribut of full story exist we dont call add attributes
    yield call(workerCartAttributes);

    yield call(checkoutRouteHandler, history);
}

function* workerProcessPaymentPage(action) {
    const {
        history,
    } = action.data;

    const userType = yield select(getUserType);

    yield put(updateShowLoadingMessage());
    yield put(pageInitializationStart());

    const flagState = yield select(getFeatureFlags);
    const isReorderFlagEnabled = flagState['is-reorder-payment-methods-enabled'];
    if (isReorderFlagEnabled) {
        yield call(fetchMostUsedPaymentMethods);
    }

    if (typeof window !== 'undefined' && !window.location.host.includes('localhost') && window.ApplePaySession) {
        yield fork(workerApplePayAvailable);
    }

    const recipients = yield select(getRecipients);
    if (recipients.length > 0) {
        yield call(onPaymentPageProcessing, action);
    }

    const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);
    if (userType === 'R' && isWriteScopeCheckout) {
        // Payment Page Dependent APIs
        yield put(loadWallet());
        yield put(loadAddressBookEntry());

        const jwtToken = yield call(checkJWT);
        const contactId = yield select(getContactId);
        const wcEnv = {
            host: mbpUtil.getEnv('APP_WC_HOST'),
        };

        yield put(loadBillingAddressBookEntry());
    }

    yield call(prepareRetreiveCart, 'payment');

    yield call(retrieveCart);

    let isSubscribe = false;
    const currentIsSubscribe = yield select(getEmailOptInSubscribeStatus);

    if (userType === 'R') {
        // Is the user is authenticated review if havee subscription
        const jwtToken = yield call(checkJWT);

        let brandId = yield select(getCurrentBrandId);
        const profile = yield select(getProfileInfo);

        if (Object.keys(brandsEmailOpt).includes(brandId)) {
            brandId = brandsEmailOpt[brandId];
        }

        const payload = {
            wcEnv: {},
            jwtToken,
            brandId,
            email: profile.email,
        };
        try {
            const responseEmail = yield call(accountServiceClient.getEmailOptInSubscribe, payload);
            if (responseEmail.data?.retrieveEmailResponse?.responseStatus === 'OK') {
                isSubscribe = responseEmail.data?.retrieveEmailResponse?.retrieveEmailResponseResult?.retrieveEmailResult?.retrieveEmailMessage === 'Active';
            }
        } catch (e) {
            isSubscribe = false;
        }
    }

    if (isSubscribe !== currentIsSubscribe) {
        yield put(setEmailOptInSubscribe(isSubscribe));
    }

    // Unchecked emailOptIn for countries different to Canada or United stated if the cookie gdpr exist
    const cookies = new Cookies();
    const gdprCookie = cookies.get('gdpr') || '';
    if (gdprCookie && !['CAN', 'USA'].includes(gdprCookie)) {
        yield put(unsubscribeEmail());
    }

    yield put(pageInitializationCompleted());
    yield call(checkoutRouteHandler, history);

    yield put(flagOffShowLoadingMessage());
}

function* workerProcessReviewOrderPage(action) {
    const {
        history,
        pageData,
    } = action.data;

    yield put(updateShowLoadingMessage());

    yield put(pageInitializationStart());

    // Validate all order items to have recipient address, delivery date and gift message.
    const isContinueToPayment = yield select(continueToPayment);
    const orderId = yield select(getOrderId);

    if (!isContinueToPayment) {
        history.push(`/checkout/shipping/${orderId}`);
    } else {
        yield call(prepareRetreiveCart, 'review');

        yield call(retrieveCart);

        const orderStatus = yield select(getOrderStatus);
        if (!cartStatusCompleteList.includes(orderStatus)) {
            if (pageData?.validateCart) {
                yield put(triggerValidateOrder(history));
                yield take(triggerValidateOrderCompleted().type);
            }

            yield call(checkoutRouteHandler, history);
            yield put(flagOffShowLoadingMessage());
        }
        yield put(pageInitializationCompleted());
    }
}

//  When the payment and review page are in only one page.
function* workerProcessPaymentReviewPage(action) {
    const {
        history,
    } = action.data;

    const userType = yield select(getUserType);

    yield put(pageInitializationStart());
    yield call(fetchMostUsedPaymentMethods);

    yield put(updateShowLoadingMessage());

    if (typeof window !== 'undefined' && !window.location.host.includes('localhost') && window.ApplePaySession) {
        yield fork(workerApplePayAvailable);
    }

    const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);
    if (userType === 'R' && isWriteScopeCheckout) {
        // Payment Page Dependent APIs
        yield put(loadWallet());
        yield put(loadAddressBookEntry());
        yield put(loadBillingAddressBookEntry());
    }

    const recipients = yield select(getRecipients);
    if (recipients.length > 0) {
        yield call(onPaymentPageProcessing, action);
    }

    // keep show loader, since retrieveCart hides loader
    yield put(updateShowLoadingMessage());

    // Need preparte cart as review for calculate shipping.
    yield call(prepareRetreiveCart, 'review');

    yield call(retrieveCart);

    yield put(pageInitializationCompleted());

    yield call(checkoutRouteHandler, history);

    // Unchecked emailOptIn for countries different to Canada or United stated if the cookie gdpr exist
    const cookies = new Cookies();
    const gdprCookie = cookies.get('gdpr') || '';

    if (gdprCookie && !['CAN', 'USA'].includes(gdprCookie)) {
        yield put(unsubscribeEmail());
    }

    yield put(flagOffShowLoadingMessage());
}

//  When one page shipping checkout active.
function* workerProcessOnePageShippingCheckout(action) {
    const {
        history,
        page,
    } = action.data;

    yield call(prepareRetreiveCart, page);
    yield call(retrieveCart, false, true);

    yield put(updateShowLoadingMessage());

    const isContinueToPayment = yield select(continueToPayment);
    const userType = yield select(getUserType);
    const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);

    if (isContinueToPayment) {
        yield put(pageInitializationStart());
        yield call(fetchMostUsedPaymentMethods);

        if (typeof window !== 'undefined' && !window.location.host.includes('localhost') && window.ApplePaySession) {
            yield fork(workerApplePayAvailable);
        }

        if (userType === 'R' && isWriteScopeCheckout) {
            // Payment Page Dependent APIs
            yield put(loadWallet());
            yield put(loadAddressBookEntry());
            yield put(loadBillingAddressBookEntry());
        }

        const recipients = yield select(getRecipients);
        if (recipients.length > 0) {
            yield call(onPaymentPageProcessing, action);
        }

        // // Unchecked emailOptIn for countries different to Canada or United stated if the cookie gdpr exist
        const cookies = new Cookies();
        const gdprCookie = cookies.get('gdpr') || '';

        if (gdprCookie && !['CAN', 'USA'].includes(gdprCookie)) {
            yield put(unsubscribeEmail());
        }

        // Click stream for one page checkout when payment expand
        const cartId = yield select(getOrderId);
        const cartOrderDetails = yield select(getCartOrder);
        const onePageCheckout = yield select(getOnePageCheckoutSaleForceABTest) || {};

        yield put(emitClickstreamCheckoutEvent({
            action: 'Impression',
            salesforceResponse: onePageCheckout,
            page: { type: 'shipping' },
            eventSubType: 'expand-payment',
            order: { cartId },
            recipients: cartOrderDetails.recipients,
        }));
    }

    yield put(pageInitializationCompleted());

    yield call(checkoutRouteHandler, history);

    yield put(flagOffShowLoadingMessage());
}

//  When one page payment checkout active.
function* workerProcessOnePagePaymentCheckout(action) {
    const {
        history,
        page,
    } = action.data;

    const userType = yield select(getUserType);

    yield put(pageInitializationStart());
    yield call(fetchMostUsedPaymentMethods);

    yield put(updateShowLoadingMessage());

    yield put(triggerValidateOrder(history, null, page));
    yield take(triggerValidateOrderCompleted().type);

    if (typeof window !== 'undefined' && !window.location.host.includes('localhost') && window.ApplePaySession) {
        yield fork(workerApplePayAvailable);
    }

    const isWriteScopeCheckout = yield select(getIsWriteScopeCheckout);

    if (userType === 'R' && isWriteScopeCheckout) {
        // Payment Page Dependent APIs
        yield put(loadWallet());
        yield put(loadAddressBookEntry());
        yield put(loadBillingAddressBookEntry());
    }

    // Need preparte cart as review for calculate shipping.
    yield call(prepareRetreiveCart, 'review');
    yield call(retrieveCart, false, true);

    // keep show loader, since retrieveCart hides loader
    yield put(updateShowLoadingMessage());

    const recipients = yield select(getRecipients);
    if (recipients.length > 0) {
        yield call(onPaymentPageProcessing, action);
    }

    // Click stream for one page checkout when payment expand
    const cartId = yield select(getOrderId);
    const cartOrderDetails = yield select(getCartOrder);
    const onePageCheckout = yield select(getOnePageCheckoutSaleForceABTest) || {};

    yield put(emitClickstreamCheckoutEvent({
        action: 'Impression',
        salesforceResponse: onePageCheckout,
        page: { type: 'shipping' },
        eventSubType: 'expand-payment',
        order: { cartId },
        recipients: cartOrderDetails.recipients,
    }));

    yield put(pageInitializationCompleted());

    yield call(checkoutRouteHandler, history);

    // Unchecked emailOptIn for countries different to Canada or United stated if the cookie gdpr exist
    const cookies = new Cookies();
    const gdprCookie = cookies.get('gdpr') || '';

    if (gdprCookie && !['CAN', 'USA'].includes(gdprCookie)) {
        yield put(unsubscribeEmail());
    }

    yield put(flagOffShowLoadingMessage());
}

function* workerConfirmationPage(action) {
    const {
        history,
    } = action.data;

    yield call(retrieveCart, true);
    yield put(getPaymentUsedToPlaceOrder());

    const orderStatus = yield select(getOrderStatus);
    const orderId = yield select(getOrderId);

    if (orderStatus && cartStatusCompleteList.includes(orderStatus)) {
        yield put(logBannerCodeTried(false));

        yield put(clearErrors({
            errType: 'payment',
            field: 'error',
        }));
    }

    // If order is pending redirect to cart page
    if (!cartStatusCompleteList.includes(orderStatus)) {
        history.push(`/checkout/cart/${orderId}`);
    }

    yield put(flagOffShowLoadingMessage());
}

function* workerAppController(action) {
    const { data: { page, history } } = action;
    let orderStatus = yield select(getOrderStatus);

    if (!orderStatus) {
        yield call(retrieveCart);
        orderStatus = yield select(getOrderStatus);
    }

    if (
        orderStatus
        && cartStatusCompleteList.includes(orderStatus)
        && page !== 'confirmation-order'
    ) {
        yield call(checkoutRouteHandler, history);
    } else if (['startShipping', 'shipping', 'cart'].includes(page)) {
        yield fork(workerProcessShippingPage, action);
    } else if (page === 'payment') {
        yield fork(workerProcessPaymentPage, action);
    } else if (page === 'review-order') {
        yield fork(workerProcessReviewOrderPage, action);
    } else if (page === 'payment-review') {
        yield fork(workerProcessPaymentReviewPage, action);
    } else if (['single-shipping-payment'].includes(page)) {
        yield fork(workerProcessOnePageShippingCheckout, action);
    } else if (page === 'single-payment-review') {
        yield fork(workerProcessOnePagePaymentCheckout, action);
    } else if (page === 'confirmation-order') {
        yield fork(workerConfirmationPage, action);
    }
}

function* watcherAppPageProcess() {
    yield takeLatest(appProcessPage().type, workerAppController);
}

const watchers = [
    fork(watcherAppPageProcess),
];

export {
    watchers,
};

export default {};
